import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button, Checkbox, Input } from '@parrot/ui';
import { AuthLayout } from './components/AuthLayout/AuthLayout';
import { AuthLink } from './components/AuthLink/AuthLink';
import styles from './LoginPage.module.css';

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  remember: z.boolean(),
});

type Credentials = z.infer<typeof schema>;

type Props = {
  /** Shown under the logo. */
  productName: string;
  /** Called on a valid submit. Throw to surface a failure. */
  onSubmit: (credentials: Credentials) => void | Promise<void>;
  onForgotPassword?: () => void;
  /** Banner above the form — a message from the previous page, e.g. a completed reset. */
  notice?: string;
  /** Omit to inherit the document's `data-theme`. Only pass to force a subtree. */
  theme?: 'dark' | 'light';
  className?: string;
};

export const LoginPage = ({
  productName,
  onSubmit,
  onForgotPassword,
  notice,
  theme,
  className,
}: Props) => {
  const [failure, setFailure] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Credentials>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: true },
  });

  const submit = handleSubmit(async (credentials) => {
    setFailure(undefined);
    try {
      await onSubmit(credentials);
    } catch (error) {
      setFailure(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.',
      );
    }
  });

  return (
    <AuthLayout
      productName={productName}
      title={'Sign In'}
      subtitle={'Welcome back'}
      notice={notice}
      failure={failure}
      theme={theme}
      className={className}
    >
      <form className={styles.form} onSubmit={submit} noValidate>
        <Input
          {...register('email')}
          label={'Email'}
          type={'email'}
          autoComplete={'email'}
          autoFocus
          placeholder={'you@company.com'}
          status={errors.email !== undefined ? 'error' : undefined}
          statusMessage={errors.email?.message}
        />

        <Input
          {...register('password')}
          label={'Password'}
          type={'password'}
          autoComplete={'current-password'}
          placeholder={'••••••••'}
          status={errors.password !== undefined ? 'error' : undefined}
          statusMessage={errors.password?.message}
        />

        <div className={styles.actions}>
          <Checkbox label={'Remember me'} size={'sm'} {...register('remember')} />
          {onForgotPassword !== undefined && (
            <AuthLink onClick={onForgotPassword}>{'Forgot password?'}</AuthLink>
          )}
        </div>

        <Button type={'submit'} size={'lg'} loading={isSubmitting} className={styles.submit}>
          {isSubmitting ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>
    </AuthLayout>
  );
};
